import type { SupabaseClient } from "@supabase/supabase-js";

// Platform active-sessions read/queue model — founder breach-response surface (Task 109 contract,
// hosted commit e994fc8). Server semantics this module preserves faithfully:
//   - platform/superadmin only; every list call is itself audited server-side;
//   - rows are redacted (user_label/account_tail, no emails, no tokens, no raw user agents) EXCEPT
//     the full session ip — that IS the legitimate breach-response use, gated to the platform role;
//   - revocation is QUEUED for a service-role worker ("queued_for_worker"). Nothing here may ever
//     claim a session is revoked — copy stays "queued" until the executor lands and reports.

export interface PlatformSessionRow {
  sessionRef: string;
  userId: string;
  userLabel: string;
  role: string;
  accountTail: string;
  surfaceHint: string;
  createdAt: string | null;
  lastRefreshedAt: string | null;
  ip: string | null;
  personDisplayName: string | null;
  accountNames: string | null;
  signInEmail: string | null;
  workspaceCount: number;
}

export interface PlatformSessionSummary {
  activeSessions: number;
  accountCount: number;
  roleCount: number;
  surfaceCount: number;
  accountTails: string[];
  roles: string[];
  surfaces: string[];
}

export interface RevokeQueued {
  ok: true;
  status: "queued_for_worker";
  requestId: string | null;
}

export type RevokeResult = RevokeQueued | { ok: false; reason: string };

/** Fixed ledger reason — deliberately not free text so no PII can be typed into the audit trail. */
const CONSOLE_REVOKE_REASON = "queued from console Platform surface";

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function asCount(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : 0;
}

export function mapPlatformSessionRow(raw: Record<string, unknown>): PlatformSessionRow {
  return {
    sessionRef: asString(raw.session_ref) ?? "",
    userId: asString(raw.user_id) ?? "",
    userLabel: asString(raw.user_label) ?? "unknown",
    role: asString(raw.role) ?? "unknown",
    accountTail: asString(raw.account_tail) ?? "—",
    surfaceHint: asString(raw.surface_hint) ?? "unknown",
    createdAt: asString(raw.created_at),
    lastRefreshedAt: asString(raw.last_refreshed_at),
    ip: asString(raw.ip),
    personDisplayName: asString(raw.person_display_name),
    accountNames: asString(raw.account_names),
    signInEmail: asString(raw.sign_in_email),
    workspaceCount: asCount(raw.workspace_count),
  };
}

function sortedUnique(values: readonly string[]): string[] {
  return [...new Set(values.filter((value) => value !== "" && value !== "unknown" && value !== "—"))].sort();
}

export function summarizePlatformSessions(
  rows: readonly PlatformSessionRow[],
  accountNameRollup?: readonly string[],
): PlatformSessionSummary {
  const accountTails = sortedUnique(rows.map((row) => row.accountTail));
  const roles = sortedUnique(rows.map((row) => row.role));
  const surfaces = sortedUnique(rows.map((row) => row.surfaceHint));
  const namedAccounts =
    accountNameRollup !== undefined ? [...accountNameRollup] : sortedUnique(
      rows.flatMap((row) => (row.accountNames ?? "").split(",").map((part) => part.trim())),
    );
  return {
    activeSessions: rows.length,
    accountCount: namedAccounts.length > 0 ? namedAccounts.length : accountTails.length,
    roleCount: roles.length,
    surfaceCount: surfaces.length,
    accountTails: namedAccounts.length > 0 ? namedAccounts : accountTails,
    roles,
    surfaces,
  };
}

/** Thrown reason is SAFE generic copy; the common denial is simply not being platform role. */
export async function fetchPlatformSessions(client: SupabaseClient): Promise<PlatformSessionRow[]> {
  const { data, error } = await client.rpc("platform_active_sessions");
  if (error) {
    throw new Error("Could not load active sessions. Platform access is required for this view.");
  }
  const rows = Array.isArray(data) ? data : [];
  return rows
    .filter((row): row is Record<string, unknown> => typeof row === "object" && row !== null)
    .map(mapPlatformSessionRow)
    .filter((row) => row.sessionRef !== "");
}

function parseQueued(data: unknown): RevokeResult {
  const record = (typeof data === "object" && data !== null ? data : {}) as Record<string, unknown>;
  if (record.ok === true && record.status === "queued_for_worker") {
    return { ok: true, status: "queued_for_worker", requestId: asString(record.request_id) };
  }
  return { ok: false, reason: "The security service did not accept the request." };
}

/** Lifecycle of a queued revocation request (Task 111 read model — safe fields only). */
export interface RevocationRequestStatus {
  requestId: string;
  scope: string;
  status: "queued" | "processing" | "succeeded" | "failed";
  requestedAt: string | null;
  updatedAt: string | null;
  completedAt: string | null;
  workerMessage: string | null;
}

export interface RevocationRequestSummary {
  total: number;
  queued: number;
  processing: number;
  succeeded: number;
  failed: number;
}

export interface RevocationRequestSnapshot {
  requestId?: string | null;
  status: RevocationRequestStatus["status"];
  message?: string | null;
}

const REQUEST_STATUS_VALUES = new Set(["queued", "processing", "succeeded", "failed"]);

export function mapRevocationStatusRow(raw: Record<string, unknown>): RevocationRequestStatus | null {
  const requestId = asString(raw.request_id);
  const status = asString(raw.status);
  if (requestId === null || status === null || !REQUEST_STATUS_VALUES.has(status)) {
    return null;
  }
  return {
    requestId,
    scope: asString(raw.scope) ?? "unknown",
    status: status as RevocationRequestStatus["status"],
    requestedAt: asString(raw.requested_at),
    updatedAt: asString(raw.updated_at),
    completedAt: asString(raw.completed_at),
    workerMessage: asString(raw.worker_message),
  };
}

/** Batched status read (≤50 ids per the hosted contract; one sequential call, pooler-safe).
 *  Errors resolve to an empty list — the chips simply keep their last known state. */
export async function fetchRevocationStatuses(
  client: SupabaseClient,
  requestIds: readonly string[],
): Promise<RevocationRequestStatus[]> {
  if (requestIds.length === 0) {
    return [];
  }
  const { data, error } = await client.rpc("platform_session_revocation_request_status", {
    request_ids: requestIds.slice(0, 50),
  });
  if (error) {
    return [];
  }
  const rows = Array.isArray(data) ? data : [];
  return rows
    .filter((row): row is Record<string, unknown> => typeof row === "object" && row !== null)
    .map(mapRevocationStatusRow)
    .filter((row): row is RevocationRequestStatus => row !== null);
}

export function summarizeRevocationRequests(
  requests: ReadonlyArray<RevocationRequestSnapshot>,
): RevocationRequestSummary {
  return requests.reduce<RevocationRequestSummary>(
    (summary, request) => ({
      ...summary,
      total: summary.total + 1,
      [request.status]: summary[request.status] + 1,
    }),
    { total: 0, queued: 0, processing: 0, succeeded: 0, failed: 0 },
  );
}

/** Queue ONE session for revocation. Server rejects self-revocation from this path. */
export async function queueRevokeSession(client: SupabaseClient, sessionRef: string): Promise<RevokeResult> {
  const { data, error } = await client.rpc("platform_revoke_session", {
    session_ref: sessionRef,
    reason: CONSOLE_REVOKE_REASON,
  });
  if (error) {
    return { ok: false, reason: "Could not queue the revoke. It was not performed." };
  }
  return parseQueued(data);
}

/** Queue sign-out-everywhere for a user. Same queued semantics. */
export async function queueRevokeUserSessions(
  client: SupabaseClient,
  targetUserId: string,
): Promise<RevokeResult> {
  const { data, error } = await client.rpc("platform_revoke_user_sessions", {
    target_user_id: targetUserId,
    reason: CONSOLE_REVOKE_REASON,
  });
  if (error) {
    return { ok: false, reason: "Could not queue the sign-out. It was not performed." };
  }
  return parseQueued(data);
}
